// src/components/RangeFilters.jsx — the Desk advanced-search price & area filters.
// Implements the two filter briefs (2026-07-05, "ფასის ფილტრი" / "ფართობის ფილტრი"):
//
// PriceFilter — min/max text inputs with comma formatting on blur, "75k"/"1.5m" suffix
//   parsing, K/M multiplier buttons INSIDE the inputs (onMouseDown+preventDefault so the
//   field never blurs), a double range slider with collision gap, and a live histogram of
//   the REAL loaded listings. Outlier rule (Zillow/Airbnb standard): the visible ceiling is
//   fixed; everything above it piles into the last "+" bucket; typing a bigger number by
//   hand is allowed — the slider thumb just parks at the end. Empty max = no upper bound.
//
// AreaFilter — same shape for m²: min/max spinner inputs + histogram slider. Custom ▲▼
//   spinners with click-acceleration (step sequence 1,1,2,2,3,5,8,10; resets after a 600ms
//   pause; never below 0). The old "desired m² ± tolerance" approximate mode was removed
//   2026-07-05 on request — agents filter by plain ranges, the ± math wasn't earning its rows.
//
// Both emit ONLY { min, max } (numbers or null = open bound) — the histogram/median/%
// arithmetic never leaves the component, exactly like the briefs' API-payload rule.

import { useMemo, useRef, useState } from 'react'
import { pill } from './desk/primitives.jsx'
import { ResultCount, resultCountText } from './desk/ResultCount.jsx'
// The bucket derivation lives in lib/ so it can be tested without a DOM — same split as
// pillFit.js. Its header carries the measured per-kind ceilings this produces.
import { rangePresets, rangePresetsFromHistogram, sigFigs } from '../lib/rangePresets.js'

const inputCls = 'w-full rounded-lg border border-input px-2 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'

// "75k" / "1.5m" / "150,000" → number (null = unparseable/empty)
export function parseHuman(str) {
  if (str == null) return null
  let t = String(str).trim().toLowerCase().replace(/,/g, '')
  if (t === '') return null
  let mult = 1
  if (t.endsWith('k')) { mult = 1e3; t = t.slice(0, -1) }
  else if (t.endsWith('m')) { mult = 1e6; t = t.slice(0, -1) }
  const v = parseFloat(t)
  return Number.isFinite(v) && v >= 0 ? v * mult : null
}
const fmt = n => (n == null ? '' : Number(n).toLocaleString('en-US'))

// ── QUICK-SELECT PRESETS — one derivation, two filters (B839, owner 2026-08-19) ──────────
// *"Same for price and same for area size."* Price had these; area had NOTHING but the two
// spin inputs and the histogram, which was the whole ask. The bucket math and the reason it
// follows deal type and property type without a branch live in `lib/rangePresets.js`; only
// the label FORMATTER differs per filter (money carries a symbol and K/M, area carries მ²),
// which is why that stayed here and the cuts did not.

// The preset chip row. `on` compares against the CURRENT bounds, so a range typed by hand that
// happens to equal a bucket lights that bucket — the control tells the truth about state rather
// than remembering what was clicked. Clicking the lit one clears, which is the only way back to
// "no bound" without emptying two fields.
function PresetRow({ presets, min, max, onChange, format }) {
  if (!presets.length) return null
  const eq = (a, b) => (a ?? null) === (b ?? null)
  return (
    <div className="flex flex-wrap gap-1.5 mb-2.5">
      {presets.map((p, i) => {
        const on = eq(min, p.min) && eq(max, p.max)
        return (
          <button key={i} type="button"
            onClick={() => on ? onChange(null, null) : onChange(p.min, p.max)}
            className={pill('none', { door: true, extra: `transition-colors ${on
              ? 'border-primary bg-primary/15 text-primary font-medium'
              : 'border-border text-muted-foreground hover:border-foreground/30'}` })}>
            {p.min == null ? `< ${format(p.max)}` : p.max == null ? `${format(p.min)}+` : `${format(p.min)}–${format(p.max)}`}
          </button>
        )
      })}
    </div>
  )
}

// Histogram buckets: buckets-1 even bins across 0..ceiling, last bucket collects everything
// > ceiling (the "+" bucket).
//
// `serverCounts` (0687, `search_meta.range_facets`) is the SAME 20-bin shape tallied over the
// WHOLE search rather than the loaded page, and when present it wins outright -- we do not blend
// it with loaded rows or fall back per-bar. They are different populations (the corpus vs the
// ~100 rows on screen) and mixing them would draw a distribution that is neither.
// The local tally stays as the fallback for every caller the server has not reached: the leads
// feed, an offline dev boot, and any response that predates the flag.
function useBuckets(values, ceiling, buckets = 20, serverCounts = null) {
  return useMemo(() => {
    if (Array.isArray(serverCounts) && serverCounts.length === buckets) {
      const srv = serverCounts.map(n => (Number.isFinite(+n) ? +n : 0))
      const srvMax = Math.max(1, ...srv)
      return srv.map(c => ({ count: c, pct: c === 0 ? 4 : Math.max(6, Math.round((c / srvMax) * 100)) }))
    }
    const counts = new Array(buckets).fill(0)
    for (const v of values) {
      if (v == null || !Number.isFinite(+v)) continue
      const n = +v
      counts[n >= ceiling ? buckets - 1 : Math.max(0, Math.floor((n / ceiling) * (buckets - 1)))]++
    }
    const max = Math.max(1, ...counts)
    return counts.map(c => ({ count: c, pct: c === 0 ? 4 : Math.max(6, Math.round((c / max) * 100)) }))
  }, [values, ceiling, buckets, serverCounts])
}

// Histogram + double slider. min/max are numbers or null (open). Slider positions clamp to
// the ceiling; dragging max fully right clears the upper bound (∞), per the brief.
function HistogramSlider({ min, max, ceiling, gap, values, onChange, formatLabel, serverBars = null, serverN = null, countUnit = 'properties', serverFloor = null }) {
  const bars = useBuckets(values, ceiling, 20, serverBars)
  const bins = bars.length - 1

  // ── THE AXIS SCALE (0697) ───────────────────────────────────────────────────────────────
  // A `floor` on the facet means the server's bins are LOG-spaced, and everything here has to
  // agree with them: the bars, the thumbs, the highlight, the tick labels and the tooltips all
  // read positions off ONE pair of functions. Get that wrong and the thumb sits at 50% while the
  // highlight ends at 90% — bars and selection describing different axes, which is worse than
  // the squashed-but-consistent chart this replaces.
  //
  // Why log at all: price is bimodal. Measured on the leads corpus 2026-08-22, rent median $700
  // (n=6,327) against sale median $103,000 (n=16,540) — 147x apart. On a linear axis 7,680
  // clusters fall in bar 0 and the whole rental market is a single bar. Log-spaced, the same
  // data draws two humps.
  //
  // No floor => linear, exactly as before. That is not only the pre-0697 fallback: it is what
  // the loaded-page tally still uses, and what every response from a server older than v54
  // carries. In linear mode posOf/valOf reduce to v/ceiling and p*ceiling, so the two modes are
  // one code path rather than two that can drift.
  const flr = Number(serverFloor)
  const logMode = Number.isFinite(flr) && flr > 0 && flr < ceiling
  const lnF = logMode ? Math.log(flr) : 0
  const lnSpan = logMode ? Math.log(ceiling) - lnF : 1

  const posOf = v => {
    if (!(v > 0)) return 0
    if (!logMode) return Math.min(1, v / ceiling)
    if (v <= flr) return 0                    // below the floor is pinned to the left edge
    return Math.min(1, (Math.log(v) - lnF) / lnSpan)
  }
  const valOf = p => {
    const q = Math.min(1, Math.max(0, p))
    return logMode ? flr * Math.exp(q * lnSpan) : q * ceiling
  }

  const lo = Math.min(min ?? 0, ceiling)
  const hi = Math.min(max ?? ceiling, ceiling)
  const loPct = posOf(lo) * 100, hiPct = posOf(hi) * 100

  // 3 significant figures — see `sigFigs` in lib/rangePresets.js, which carries the reason it is
  // NOT `Math.round(v / m) * m` (that form put the float error back and shipped a tooltip reading
  // "41.3000000000000004მ² – 48.400000000000006მ²").
  const sig = sigFigs
  // Bar tooltip: WHICH range this bar covers, not only how many are in it. Edges come from the
  // SCALE, so in log mode they are geometric and the first bar starts at the floor, not at 0.
  // The last bin is the "+" overflow — it has no upper edge, so it must not be given one.
  const barTitle = (i, count) => {
    const span = i >= bins
      ? `${formatLabel(sig(ceiling))}+`
      : `${formatLabel(sig(valOf(i / bins)))} – ${formatLabel(sig(valOf((i + 1) / bins)))}`
    return `${span} · ${resultCountText(count, countUnit)}`
  }

  // The native range inputs work in POSITION space (0..STEPS) and are converted through the
  // scale, because a value-space slider cannot express a log axis. In linear mode STEPS=1000 is
  // exactly the ceiling/1000 step this replaces, so drag feel there is unchanged.
  const STEPS = 1000
  const stepOf = v => Math.round(posOf(v) * STEPS)
  function slide(which, step) {
    const v = valOf(step / STEPS)
    let a = lo, b = hi
    if (which === 'min') a = Math.min(v, b - gap)
    else b = Math.max(v, a + gap)
    a = Math.max(0, a); b = Math.min(ceiling, b)
    // Far left still means NO LOWER BOUND. In log mode position 0 is the floor rather than zero,
    // and an agent who drags to the end means "no minimum" in either mode — so the test is the
    // slider position, not the value it maps to.
    const noMin = which === 'min' ? step <= 0 : a <= 0
    onChange(noMin ? null : Math.round(a), b >= ceiling ? null : Math.round(b))
  }

  return (
    <div>
      <div className="flex items-end justify-between h-8 px-1 mb-1 gap-px">
        {bars.map((b, i) => {
          const pos = i / bins
          const active = pos >= posOf(lo) - 1e-9 && pos <= posOf(hi) + 1e-9
          return <div key={i} title={barTitle(i, b.count)} style={{ height: `${b.pct}%` }}
            className={`flex-1 rounded-t ${active ? 'bg-primary' : 'bg-border'} transition-colors`} />
        })}
      </div>
      <div className="relative h-5 sntv-dual-range">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded bg-border" />
        <div className="absolute top-1/2 -translate-y-1/2 h-1 rounded bg-primary"
          style={{ left: `${loPct}%`, width: `${Math.max(0, hiPct - loPct)}%` }} />
        <input type="range" min="0" max={STEPS} step="1" value={stepOf(lo)}
          onChange={e => slide('min', +e.target.value)} />
        <input type="range" min="0" max={STEPS} step="1" value={stepOf(hi)}
          onChange={e => slide('max', +e.target.value)} />
      </div>
      <div className="flex justify-between text-xs text-muted-foreground mt-1">
        {/* In log mode the left end of the axis is the FLOOR, not zero. Printing "$0" there would
            name a value the axis does not reach and the bars do not describe. */}
        <span>{formatLabel(logMode ? sig(flr) : 0)}</span>
        {/* What the bars are counting. Shown ONLY for a server histogram: on the loaded-page
            fallback this number would describe the PAGE rather than the search, which is the
            confusion the whole change exists to remove.
            ResultCount owns the label, and with it rule 1 from its own header — the unit is
            CLUSTERS, so it must be named and never rendered as a bare number. It also picks the
            leads wording from `unit`, which a hardcoded key would have got wrong on that panel.
            ⚠️ NOT t(): `t` takes a key only and does not interpolate, so t(key, {n}) renders the
            literal "{n} ობიექტი". tLog is the interpolating one, and ResultCount already uses it. */}
        {Number.isFinite(serverN) && <ResultCount total={serverN} unit={countUnit} inline />}
        <span>{formatLabel(ceiling)}+</span>
      </div>
      {/* Two stacked native ranges: track ignores the pointer, only the thumbs take it —
          the standard dual-slider trick, scoped by the wrapper class. */}
      <style>{`
        .sntv-dual-range input[type=range]{position:absolute;inset:0;width:100%;margin:0;-webkit-appearance:none;appearance:none;background:transparent;pointer-events:none;height:100%}
        .sntv-dual-range input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;pointer-events:auto;width:16px;height:16px;border-radius:9999px;background:hsl(var(--background,0 0% 100%));border:2px solid currentColor;cursor:pointer;color:inherit}
        .sntv-dual-range input[type=range]::-moz-range-thumb{pointer-events:auto;width:12px;height:12px;border-radius:9999px;background:white;border:2px solid currentColor;cursor:pointer}
      `}</style>
    </div>
  )
}

// Text input with comma formatting on blur/Enter. k/m suffix parsing (parseHuman) still works if
// typed by hand — only the K/M quick-multiply buttons inside the field were removed (2026-07-07).
function MoneyInput({ value, placeholder, onCommit }) {
  const [text, setText] = useState(null)   // null = not editing → show formatted prop
  const shown = text != null ? text : fmt(value)
  const commit = t => { setText(null); onCommit(parseHuman(t)) }
  return (
    <input className={`${inputCls} flex-1`} inputMode="decimal" placeholder={placeholder} value={shown}
      onChange={e => setText(e.target.value)}
      onBlur={e => commit(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') { commit(e.currentTarget.value); e.currentTarget.blur() } }} />
  )
}

// `label` rides inside the min placeholder (e.g. "ფასი · min $") so the panel needs no
// separate header line above the filter (2026-07-12, space-saving pass).
// `presetValues` defaults to `values` and is withdrawn (null) by the caller when the current
// sort makes the loaded page a biased sample of THIS quantity — see the note at priceValues in
// IntakeTab. The HISTOGRAM keeps `values` either way: it draws the page and never claims to be
// the market, whereas a preset chip is exactly such a claim.
// `facet` = this currency's slice of `search_meta.range_facets` (0687): {ceiling, buckets, n},
// tallied over the WHOLE search instead of the loaded page. When present its ceiling wins -- and
// that matters more than the bars do, because the ceiling also sets the slider's span AND the
// preset chips. Measured 2026-08-21 on the 29,537-cluster corpus: the loaded-page sample put the
// building-area axis at 2000m2 where the corpus says 200m2, so every real flat sat in bar 1-2.
export function PriceFilter({ min, max, currency, values, presetValues = values, onChange, ceiling = 1_000_000, gap = 10_000, label = '', facet = null, countUnit = 'properties' }) {
  const srvCeil = Number(facet?.ceiling)
  const ceil = Number.isFinite(srvCeil) && srvCeil > 0 ? srvCeil : ceiling
  // The gap is a proportion of the axis, so it must follow a server ceiling rather than keep the
  // value the caller derived from the loaded-page one.
  const g = ceil === ceiling ? gap : Math.max(1, Math.round(ceil / 100))
  const sym = { USD: '$', GEL: '₾', EUR: '€' }[currency] || currency
  const short = n => n >= 1e6 ? `${sym}${n / 1e6}M` : n >= 1e3 ? `${sym}${n / 1e3}K` : `${sym}${n}`
  // Popular-range presets (eBay/Zillow "Buying Format" pattern) — see rangePresets above for
  // why the ceiling alone makes these follow deal type and property type.
  // `values` is the SAME array the histogram draws, so the chips and the bars describe one
  // distribution. Passing it is what moves the cuts from the axis onto the stock.
  // 0687: the presets follow the CORRECTED ceiling. `presetValues` is left exactly as it is --
  // withdrawing the chips when the loaded page is a biased sample is the same defect from the
  // other end, and it stays right even once the axis itself is corpus-true.
  // The chips follow whichever distribution the BARS are drawing, so the two controls never
  // describe different pools (rangePresets' own invariant). With a server facet that is the
  // corpus histogram; otherwise it stays the loaded values, including upstream's withdrawal of
  // them when the current sort makes the loaded page a biased sample.
  const presets = useMemo(
    () => (facet?.buckets ? rangePresetsFromHistogram(ceil, facet.buckets, facet.floor ?? null) : rangePresets(ceil, presetValues)),
    [ceil, presetValues, facet])
  return (
    <div>
      <PresetRow presets={presets} min={min} max={max} onChange={onChange} format={short} />
      <div className="flex flex-col gap-1.5 mb-2">
        <MoneyInput value={min} placeholder={`${label ? label + ' · ' : ''}min ${sym}`} onCommit={v => onChange(v, max)} />
        <MoneyInput value={max} placeholder={`max ${sym} (empty = ∞)`} onCommit={v => onChange(min, v)} />
      </div>
      <HistogramSlider min={min} max={max} ceiling={ceil} gap={g} values={values}
        onChange={onChange} formatLabel={short} serverBars={facet?.buckets ?? null} serverN={facet?.n ?? null}
        countUnit={countUnit} serverFloor={facet?.floor ?? null} />
    </div>
  )
}

// Price per m² (core 3872, owner 2026-10-24): two boxes in the price's currency, read like the price boxes (MoneyInput).
// No histogram: the search has no per-m² tally to draw, and bars of the loaded page would claim to be the market.
export function PpmFilter({ min, max, currency, onChange, labels }) {
  const sym = { USD: '$', GEL: '₾', EUR: '€' }[currency] || currency
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs text-muted-foreground">{labels.title}</div>
      <div className="flex gap-1.5">
        <MoneyInput value={min} placeholder={`${labels.min} ${sym}/მ²`} onCommit={v => onChange(v, max)} />
        <MoneyInput value={max} placeholder={`${labels.max} ${sym}/მ²`} onCommit={v => onChange(min, v)} />
      </div>
    </div>
  )
}

// ▲▼ spinner input with click-acceleration: fast consecutive clicks (or held arrow keys)
// step through 1,1,2,2,3,5,8,10; a 600ms pause resets to 1. Value floor-clamped at 0.
const ACCEL = [1, 1, 2, 2, 3, 5, 8, 10]
function SpinInput({ value, placeholder, onCommit, suffix = 'მ²' }) {
  const [text, setText] = useState(null)
  const accel = useRef({ t: 0, i: 0 })
  const shown = text != null ? text : (value ?? '')
  const commit = t => { setText(null); const v = parseHuman(t); onCommit(v == null ? null : Math.max(0, v)) }
  const bump = dir => {
    const now = Date.now()
    accel.current.i = now - accel.current.t <= 600 ? Math.min(accel.current.i + 1, ACCEL.length - 1) : 0
    accel.current.t = now
    const step = ACCEL[accel.current.i]
    const cur = parseHuman(text != null ? text : String(value ?? '')) ?? 0
    const next = Math.max(0, cur + dir * step)
    setText(null); onCommit(next)
  }
  return (
    <div className="relative flex-1">
      <input className={`${inputCls} pr-14`} inputMode="numeric" placeholder={placeholder} value={shown}
        onChange={e => setText(e.target.value)}
        onBlur={e => commit(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { commit(e.currentTarget.value); e.currentTarget.blur() }
          if (e.key === 'ArrowUp') { e.preventDefault(); bump(1) }
          if (e.key === 'ArrowDown') { e.preventDefault(); bump(-1) }
        }} />
      <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{suffix}</span>
      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col">
        {[['▲', 1], ['▼', -1]].map(([g, d]) => (
          <button key={g} type="button" tabIndex={-1}
            className="text-xs leading-[10px] px-1 text-muted-foreground hover:text-foreground"
            onMouseDown={e => { e.preventDefault(); bump(d) }}>{g}</button>
        ))}
      </div>
    </div>
  )
}

// Same `facet` contract as PriceFilter -- see its header. Area has no currency, so IntakeTab
// hands it the '_' slice.
export function AreaFilter({ min, max, values, presetValues = values, onChange, ceiling = 500, gap = 5, label = '', facet = null, countUnit = 'properties' }) {
  const srvCeil = Number(facet?.ceiling)
  const ceil = Number.isFinite(srvCeil) && srvCeil > 0 ? srvCeil : ceiling
  const g = ceil === ceiling ? gap : Math.max(1, Math.round(ceil / 100))
  // B839's actual ask. Same derivation as price, same chip row — the ceiling is already
  // kind/intent-aware (niceCeiling over areaValues, and a separate one for land), so an
  // apartment search offers ~40/80/120m² buckets where a plot search offers hundreds.
  // ⚠️ Rendered for the LAND instance too, deliberately: it takes its own `landAreaCeiling`,
  // which hugs a completely different cluster, so the two rows never show the same numbers.
  // `values` is the SAME array the histogram draws, so the chips and the bars describe one
  // distribution. Passing it is what moves the cuts from the axis onto the stock.
  // 0687: the presets follow the CORRECTED ceiling. `presetValues` is left exactly as it is --
  // withdrawing the chips when the loaded page is a biased sample is the same defect from the
  // other end, and it stays right even once the axis itself is corpus-true.
  // The chips follow whichever distribution the BARS are drawing, so the two controls never
  // describe different pools (rangePresets' own invariant). With a server facet that is the
  // corpus histogram; otherwise it stays the loaded values, including upstream's withdrawal of
  // them when the current sort makes the loaded page a biased sample.
  const presets = useMemo(
    () => (facet?.buckets ? rangePresetsFromHistogram(ceil, facet.buckets, facet.floor ?? null) : rangePresets(ceil, presetValues)),
    [ceil, presetValues, facet])
  const short = n => `${n >= 1000 ? `${+(n / 1000).toFixed(1)}k` : n}მ²`
  return (
    <div>
      <PresetRow presets={presets} min={min} max={max} onChange={onChange} format={short} />
      <div className="flex flex-col gap-1.5 mb-2">
        <SpinInput value={min} placeholder={`${label ? label + ' · ' : ''}min`} onCommit={v => onChange(v, max)} />
        <SpinInput value={max} placeholder="max" onCommit={v => onChange(min, v)} />
      </div>
      <HistogramSlider min={min} max={max} ceiling={ceil} gap={g} values={values}
        onChange={onChange} formatLabel={n => `${n}მ²`} serverBars={facet?.buckets ?? null} serverN={facet?.n ?? null}
        countUnit={countUnit} serverFloor={facet?.floor ?? null} />
    </div>
  )
}
